import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Referral } from '@/types/referral.types';
import { ReferralStatusBadge } from './ReferralStatusBadge';

interface ReferralCardProps {
  referral: Referral;
  type: 'sent' | 'received';
  onAccept?: (id: string) => void;
  onReject?: (id: string) => void;
  onCancel?: (id: string) => void;
  onComplete?: (id: string) => void;
  className?: string;
}

export const ReferralCard: React.FC<ReferralCardProps> = ({
  referral,
  type,
  onAccept,
  onReject,
  onCancel,
  onComplete,
  className = '',
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleCardClick = () => {
    navigate(`/referrals/${referral.id}`);
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const otherBroker = type === 'sent' ? referral.to_broker : referral.from_broker;
  const splitPercent = type === 'sent' ? referral.sender_split_percent : referral.receiver_split_percent;

  return (
    <div
      onClick={handleCardClick}
      className={`bg-white rounded-xl border border-secondary-200 shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer ${className}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs text-secondary-500">#{referral.referral_number}</p>
          <h3 className="text-sm font-semibold text-secondary-900 mt-0.5">
            {referral.lead.name}
          </h3>
        </div>
        <ReferralStatusBadge status={referral.status} size="sm" />
      </div>

      {/* Lead Phone */}
      <div className="flex items-center gap-2 text-sm text-secondary-600 mb-2">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
        <span>{referral.lead.phone}</span>
      </div>

      {/* Broker Info */}
      <div className="flex items-center gap-2 text-sm text-secondary-600 mb-3">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span>
          {type === 'sent' ? 'To: ' : 'From: '}
          <span className="font-medium text-secondary-900">{otherBroker.full_name}</span>
          {otherBroker.firm_name && (
            <span className="text-secondary-500"> ({otherBroker.firm_name})</span>
          )}
        </span>
      </div>

      {/* Split and Date */}
      <div className="flex items-center justify-between text-xs text-secondary-500 mb-3">
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium text-secondary-700">Your split: {splitPercent}%</span>
        </div>
        <span>{formatDate(referral.created_at)}</span>
      </div>

      {/* Commission Split Visual */}
      <div className="h-2 rounded-full overflow-hidden bg-secondary-100 mb-3">
        <div
          className={`h-full transition-all duration-300 ${type === 'sent' ? 'bg-primary-500' : 'bg-emerald-500'}`}
          style={{ width: `${splitPercent}%` }}
        />
      </div>

      {/* Actions */}
      {referral.status === 'PENDING' && (
        <div className="flex gap-2 pt-2 border-t border-secondary-100">
          {type === 'received' && (
            <>
              {onAccept && (
                <button
                  onClick={(e) => handleActionClick(e, () => onAccept(referral.id))}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  Accept
                </button>
              )}
              {onReject && (
                <button
                  onClick={(e) => handleActionClick(e, () => onReject(referral.id))}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  Reject
                </button>
              )}
            </>
          )}
          {type === 'sent' && onCancel && (
            <button
              onClick={(e) => handleActionClick(e, () => onCancel(referral.id))}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-secondary-600 bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {referral.status === 'ACCEPTED' && onComplete && (
        <div className="flex gap-2 pt-2 border-t border-secondary-100">
          <button
            onClick={(e) => handleActionClick(e, () => onComplete(referral.id))}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Mark Complete
          </button>
        </div>
      )}
    </div>
  );
};

export default ReferralCard;
